// Frontend encryption/decryption utilities
// This handles RSA decryption for end-to-end encrypted messages

export interface KeyPair {
  publicKey: string;
  privateKey: string;
}

// Generate RSA key pair (2048-bit)
export async function generateKeyPair(): Promise<KeyPair> {
  const keyPair = await window.crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 2048,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt']
  );

  const publicKeyPem = await window.crypto.subtle.exportKey('spki', keyPair.publicKey);
  const privateKeyPem = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

  return {
    publicKey: arrayBufferToPem(publicKeyPem, 'PUBLIC KEY'),
    privateKey: arrayBufferToPem(privateKeyPem, 'PRIVATE KEY')
  };
}

// Convert ArrayBuffer to PEM format
function arrayBufferToPem(buffer: ArrayBuffer, type: string): string {
  const bytes = new Uint8Array(buffer);
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(bytes)));
  return `-----BEGIN ${type}-----\n${base64.match(/.{1,64}/g)?.join('\n')}\n-----END ${type}-----\n`;
}

// Convert PEM to ArrayBuffer
function pemToArrayBuffer(pem: string): ArrayBuffer {
  const base64 = pem
    .replace(/-----BEGIN [^-]+-----/g, '')
    .replace(/-----END [^-]+-----/g, '')
    .replace(/\s/g, '');
  
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

// Decrypt message using private key
export async function decryptMessage(encryptedContent: string, privateKeyPem: string): Promise<string> {
  try {
    // Import private key
    const privateKeyBuffer = pemToArrayBuffer(privateKeyPem);
    const privateKey = await window.crypto.subtle.importKey(
      'pkcs8',
      privateKeyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['decrypt']
    );

    // Decode base64 encrypted content
    const encryptedBuffer = Uint8Array.from(atob(encryptedContent), c => c.charCodeAt(0));

    // Decrypt
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'RSA-OAEP',
      },
      privateKey,
      encryptedBuffer
    );

    // Convert to string
    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    console.error('Decryption failed:', error);
    return encryptedContent; // Return original if decryption fails
  }
}

// Encrypt message using public key
export async function encryptMessage(content: string, publicKeyPem: string): Promise<string> {
  try {
    // Import public key
    const publicKeyBuffer = pemToArrayBuffer(publicKeyPem);
    const publicKey = await window.crypto.subtle.importKey(
      'spki',
      publicKeyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['encrypt']
    );

    // Encrypt
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      publicKey,
      new TextEncoder().encode(content)
    );

    // Convert to base64
    const bytes = new Uint8Array(encryptedBuffer);
    return btoa(String.fromCharCode.apply(null, Array.from(bytes)));
  } catch (error) {
    console.error('Encryption failed:', error);
    return content; // Return original if encryption fails
  }
}
